import { z } from 'zod';
/**
 * メーラー（Gmail API）と Google Workspace の OAuth 連携。
 * 受信箱は組織で 1 つ（例：info@）を OAuth で接続し、利用者全員で共有する。
 */
/** 接続時に求めるスコープ。読み取り・ラベル変更・送信を gmail.modify でまかなう */
export const GOOGLE_WORKSPACE_SCOPES = [
    'openid',
    'email',
    'profile',
    'https://www.googleapis.com/auth/gmail.modify',
];
/**
 * Google Workspace 連携の設定（integration_settings.config）。
 * クライアントシークレットは保存時に暗号化する。リフレッシュトークンは encrypted_key に、
 * 接続したアドレスは masked_key に入れる。
 */
export const googleWorkspaceConfigSchema = z.object({
    clientId: z.string().trim().max(300).default(''),
    /** 保存時に暗号化する。読み出しでは返さない */
    clientSecret: z.string().max(500).optional(),
    /** 指定すると、このドメインのアカウントだけ接続できる（例：n-t-p.io） */
    hostedDomain: z
        .string()
        .trim()
        .toLowerCase()
        .regex(/^([a-z0-9-]+\.)+[a-z]{2,}$/, 'ドメインの形式が正しくありません（例：example.co.jp）')
        .or(z.literal(''))
        .default(''),
    /** 以下は接続時にサーバーが書く */
    connectedEmail: z.string().default(''),
    connectedName: z.string().default(''),
    connectedAt: z.string().default(''),
    grantedScopes: z.string().default(''),
});
// ---------------------------------------------------------------- メーラー
/** 画面左の固定ラベル。値は Gmail のシステムラベル ID */
export const MAILER_FOLDERS = [
    { id: 'INBOX', label: '受信トレイ' },
    { id: 'STARRED', label: 'スター付き' },
    { id: 'SENT', label: '送信済み' },
    { id: 'DRAFT', label: '下書き' },
    { id: 'ALL', label: 'すべてのメール' },
    { id: 'SPAM', label: '迷惑メール' },
    { id: 'TRASH', label: 'ゴミ箱' },
];
export const mailerThreadsQuerySchema = z.object({
    folder: z.enum(MAILER_FOLDERS.map((f) => f.id)).default('INBOX'),
    /** Gmail の検索演算子（from: has:attachment など）がそのまま使える */
    q: z.string().trim().max(500).optional(),
    pageToken: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(25),
});
export const MAILER_THREAD_ACTIONS = [
    'read',
    'unread',
    'star',
    'unstar',
    'archive',
    'inbox',
    'trash',
    'untrash',
    'spam',
    'notSpam',
];
export const mailerThreadActionSchema = z.object({
    threadIds: z.array(z.string().min(1).max(100)).min(1).max(100),
    action: z.enum(MAILER_THREAD_ACTIONS),
});
const addressList = z
    .array(z.string().trim().toLowerCase().email('メールアドレスの形式が正しくありません'))
    .max(50)
    .default([]);
export const MAILER_COMPOSE_MODES = ['new', 'reply', 'replyAll', 'forward'];
/** 送信。添付はマルチパートの files で送り、この内容は payload（JSON 文字列）に入れる */
export const mailerSendSchema = z
    .object({
    mode: z.enum(MAILER_COMPOSE_MODES).default('new'),
    to: addressList,
    cc: addressList,
    bcc: addressList,
    subject: z.string().trim().max(500).default(''),
    body: z.string().max(200_000).default(''),
    /** 返信・転送の元になるメッセージ（Gmail のメッセージ ID） */
    sourceMessageId: z.string().max(100).optional(),
    /** 返信で元のメールを引用する（Gmail の「…」と同じ） */
    includeQuote: z.boolean().default(true),
    /** 転送で元の添付を付ける */
    includeAttachments: z.boolean().default(true),
})
    .refine((v) => v.to.length + v.cc.length + v.bcc.length > 0, {
    message: '宛先を 1 件以上入力してください',
    path: ['to'],
})
    .refine((v) => v.mode === 'new' || !!v.sourceMessageId, {
    message: '返信・転送の元になるメールを指定してください',
    path: ['sourceMessageId'],
});
/** AI 下書き。返信・転送は元のメール（とそのスレッド）を踏まえて書く */
export const mailerDraftSchema = z
    .object({
    mode: z.enum(MAILER_COMPOSE_MODES).default('new'),
    sourceMessageId: z.string().max(100).optional(),
    /** 伝えたい要点（例：火曜 14 時で了承、資料を添付した旨） */
    instruction: z.string().trim().max(1000).default(''),
    tone: z.enum(['polite', 'concise']).default('polite'),
    /** 書きかけの本文。あれば清書する（署名は除いて渡す） */
    currentBody: z.string().max(10_000).default(''),
    subject: z.string().trim().max(500).default(''),
    to: z.array(z.string().max(320)).max(50).default([]),
})
    .refine((v) => v.mode === 'new' || !!v.sourceMessageId, {
    message: '返信・転送の元になるメールを指定してください',
    path: ['sourceMessageId'],
})
    .refine((v) => v.mode !== 'new' || !!v.instruction || !!v.currentBody.trim(), {
    message: '新規作成では、伝えたい要点を入力してください',
    path: ['instruction'],
});
//# sourceMappingURL=mailer.js.map